import {
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import isNil from "lodash/isNil";
import omit from "lodash/omit";
import omitBy from "lodash/omitBy";
import isEmpty from "lodash/isEmpty";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { Flex } from "../../components/Flex";
import { SpinnerFullScreen } from "../../components/SpinnerFullScreen";
import { SubHeader } from "../../components/SubHeader";
import { DNDContext } from "../../components/dnd/DNDContext";
import { DNDSortableDroppableContext } from "../../components/dnd/DNDSortableDroppableContext";
import i18n from "../../i18n";
import { KanbanColumn as KanbanColumnModel } from "../../model/kanban/KanbanColumn";
import { UpdateKanbanCardDto } from "../../model/kanban/dto/UpdateKanbanCardDto";
import { KanbanColumnsCount } from "../../model/kanban/types/KanbanColumnsCount.enum";
import { kanbanSliceSelectors } from "../../redux/kanban/kanban.selector";
import { kanbanActions } from "../../redux/kanban/kanban.slice";
import { permissionActions } from "../../redux/permission/permission.slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { thresholdSliceSelectors } from "../../redux/threshold/threshold.selector";
import { thresholdActions } from "../../redux/threshold/threshold.slice";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { Spacing } from "../../theme/Spacing";
import { AtiraToast } from "../../utils/AtiraToast";
import {
  getNewColumnsStateForDifferentColumnsDND,
  getNewColumnsStateForSameColumnDND,
  getNewKanbanStateForColumnsDND,
} from "./KanbanUtils";
import { KanbanCard } from "./components/KanbanCard";
import { KanbanColumn } from "./components/KanbanColumn";
import { KanbanColumnAdd } from "./components/KanbanColumnAdd";
import { KanbanHeader } from "./components/KanbanHeader";
import { faBolt } from "@fortawesome/free-solid-svg-icons/faBolt";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import { useDrawersContext } from "../tasks/drawers-context";
import isFunction from "lodash/isFunction";
import { EntityIcons } from "../../utils/GlobalIcons";
import { AtiraDropdown } from "../../components/AtiraDropdown";
import { faBars } from "@fortawesome/free-solid-svg-icons/faBars";
import { Button } from "../../components/Button";
import { MenuProps } from "../../utils/local-antd";
import { AtiraIcon } from "../../components/AtiraIcon";
import { Text } from "../../components/Text";
import { AppLangs } from "../../model/shared/enum/AppLangs.enum";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faTimeline } from "@fortawesome/free-solid-svg-icons/faTimeline";
import { FormProvider, useForm } from "react-hook-form";
import { GetUserKanbanDto } from "../../model/kanban/dto/GetUserDefaultKanbanDto";
import { KanbanCardFilterRange } from "../../model/kanban/types/KanbanCardFilterRange.enum";
import { KanbanColumnStageType } from "../../model/kanban/types/KanbanColumnStageType.enum";
import { KanbanDeleteModal } from "./components/KanbanDeleteModal";
import { TestIDs } from "../../tests/types/testIds";
import { faFile } from "@fortawesome/free-regular-svg-icons/faFile";

const ColumnsWrapper = styled(Flex)`
  direction: ltr !important;
  padding: ${Spacing.m};
  flex: 1;
  overflow-x: auto;
  background-color: ${({ theme }) => theme.sub};

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.sub};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.main};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.lightMain};
  }

  > * {
    flex-shrink: 0;
  }
`;

enum DraggedItemTypes {
  COLUMN = "COLUMN",
  CARD = "CARD",
}

const KanbanRoute: React.FC = () => {
  /** Item being picked */
  const [activeItemId, setActiveItemId] = useState<
    UniqueIdentifier | undefined
  >();
  const [columns, setColumns] = useState<KanbanColumnModel[]>([]);
  const [draggedItemType, setDraggedItemType] =
    useState<DraggedItemTypes | null>(null);
  const [kanbanDeleteVisible, setKanbanDeleteVisible] = useState(false);
  const kanbanWrapperRef = useRef<HTMLDivElement | null>(null);
  const columnRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  // Set once from a report drill-through, consumed as soon as the matching
  // column exists in `columns` (the board loads after this effect runs).
  const pendingScrollStage = useRef<KanbanColumnStageType | undefined>(
    undefined,
  );

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const {
    setKanbanCardMovingReasonVisible,
    setKanbanSettingsVisible,
    setKanbanAutomationVisible,
    setKanbanActivityVisible,
    setKanbanCreateVisible,
    onDragKanbanCardWithReasonMessage,
    setOnDragKanbanCardWithReasonMessage,
    setKanbanCardMovingReasonStageType,
    setSubToUnlock,
    setExportModalVisible,
  } = useDrawersContext();

  const kanban = useAppSelector(kanbanSliceSelectors.selectUserCurrentKanban);
  const kanbanLoading = useAppSelector(
    kanbanSliceSelectors.selectUserKanbanLoading,
  );
  const kanbansMeta = useAppSelector(kanbanSliceSelectors.selectKanbansMeta);
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const userPermissions = user.permissions.permissions;
  const thresholds = useAppSelector(
    thresholdSliceSelectors.selectThresholdStats,
  );
  const isMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);

  // 18 for everyone till we add this in the thresholds
  const canAddColumn = columns.length < KanbanColumnsCount.PAID;

  const hasMultipleKanbans = kanbansMeta?.length > 1;

  const canCreateKanban =
    (thresholds?.consumed.KANBANS || 0) < (thresholds?.total.KANBANS || 0);

  const formHeaderMethods = useForm<GetUserKanbanDto>({
    defaultValues: {
      range: KanbanCardFilterRange.ALL,
      kanbanId: kanban?._id,
    },
  });

  const getColumnByCardId = (itemId: UniqueIdentifier) => {
    for (let i = 0; i < columns?.length; i++) {
      const col = columns[i];
      if (col?.cards?.find((card) => card._id === itemId)) {
        return col;
      }
    }
  };

  const getColumnById = (itemId: UniqueIdentifier) => {
    return columns.find(({ _id }) => _id === itemId);
  };

  const onDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveItemId(active.id);

    if (getColumnById(active.id)) {
      setDraggedItemType(DraggedItemTypes.COLUMN);
    } else {
      setDraggedItemType(DraggedItemTypes.CARD);
    }
  };

  const onDragCardConfirm = async (
    event: DragEndEvent,
    reasonMessage?: string,
  ) => {
    try {
      const { active, over } = event;

      const currentColumn =
        getColumnByCardId(over?.id!) || getColumnById(over?.id!);
      const sourceColumn = getColumnByCardId(active.id);

      if (!currentColumn || !over || active.id === over.id) {
        return;
      }

      let newColumnsState: {
        newOrder?: number;
        oldOrder?: number;
        newColumns: KanbanColumnModel[];
      };
      // If the card is being moved within the same column
      if (!sourceColumn || sourceColumn._id === currentColumn._id) {
        newColumnsState = getNewColumnsStateForSameColumnDND({
          currentColumn,
          columns,
          active,
          over,
        });
        if (newColumnsState.newOrder === newColumnsState.oldOrder) {
          newColumnsState.newOrder = undefined;
          newColumnsState.oldOrder = undefined;
        }
      } else {
        newColumnsState = getNewColumnsStateForDifferentColumnsDND({
          currentColumn,
          sourceColumn,
          columns,
          active,
          over,
        });
      }

      setColumns(newColumnsState.newColumns);

      const dto = omitBy(
        {
          cardId: active.id as string,
          columnId: currentColumn._id,
          userId: user._id,
          newOrder: newColumnsState.newOrder,
          oldOrder: newColumnsState.oldOrder,
          kanbanId: kanban?._id!,
          ...(reasonMessage ? { movingReason: reasonMessage } : {}),
        },
        isNil,
      ) as unknown as UpdateKanbanCardDto;

      await dispatch(kanbanActions.updateKanbanCard(dto)).unwrap();

      if (reasonMessage) {
        AtiraToast.success(t("deals.drawer.moving_reason.success"));
      }

      setOnDragKanbanCardWithReasonMessage(null);
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setActiveItemId(undefined);
      setDraggedItemType(null);
    }
  };

  const onDragEndColumn = async (event: DragEndEvent) => {
    try {
      const { active, over } = event;

      const movedColumn = getColumnById(active.id);
      const overColumn =
        getColumnById(over?.id!) || getColumnByCardId(over?.id!);

      if (!over || active.id === over.id || !movedColumn || !overColumn) {
        setActiveItemId(undefined);
        return;
      }

      const newColumnsState = getNewKanbanStateForColumnsDND({
        columns,
        activeCol: movedColumn,
        overCol: overColumn,
      });

      setColumns(newColumnsState.newColumns);

      setActiveItemId(undefined);

      await dispatch(
        kanbanActions.updateKanbanColumnById({
          columnId: movedColumn._id,
          kanbanId: kanban?._id!,
          userId: user._id,
          oldOrder: newColumnsState.oldOrder,
          newOrder: newColumnsState.newOrder,
        }),
      ).unwrap();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setDraggedItemType(null);
    }
  };

  const onDragEndCard = (event: DragEndEvent) => {
    const { over, active } = event;

    const currentColumn =
      getColumnByCardId(over?.id!) || getColumnById(over?.id!);
    const sourceColumn = getColumnByCardId(active.id);

    const defaultColumns = columns.filter((column) => column.default);

    const showModal = defaultColumns.find(
      (defaultColumn) =>
        defaultColumn._id === currentColumn?._id &&
        currentColumn._id !== sourceColumn?._id,
    );

    if (showModal) {
      if (!isFunction(onDragKanbanCardWithReasonMessage)) {
        setOnDragKanbanCardWithReasonMessage(
          () => (reason: string) => onDragCardConfirm(event, reason),
        );
      }
      setKanbanCardMovingReasonStageType(showModal.stageType || null);
      setKanbanCardMovingReasonVisible(true);
    } else {
      onDragCardConfirm(event);
    }
  };

  const renderDragOverlay = () => {
    if (!activeItemId) {
      return null;
    }

    if (draggedItemType === DraggedItemTypes.CARD) {
      const activeCard = columns
        .flatMap((col) => col.cards)
        .find((card) => card?._id === activeItemId);

      if (!activeCard) {
        return null;
      }

      return <KanbanCard card={activeCard} />;
    } else {
      const activeColumn = columns.find((col) => col._id === activeItemId);
      if (!activeColumn) {
        return null;
      }

      return (
        <Flex height={"30rem"}>
          <KanbanColumn column={activeColumn} />
        </Flex>
      );
    }
  };

  const moveColumn = async (index: number, direction: "left" | "right") => {
    try {
      const targetIndex =
        // eslint-disable-next-line no-nested-ternary
        i18n.language === AppLangs.AR
          ? direction === "left"
            ? index + 1
            : index - 1
          : direction === "left"
            ? index - 1
            : index + 1;

      const movedcolumnId = columns.find((col) => col.order === index)?._id;
      const canMove =
        targetIndex >= 0 &&
        targetIndex < columns.length &&
        (movedcolumnId?.length || 0) > 0;

      if (canMove) {
        const res = await dispatch(
          kanbanActions.updateKanbanColumnById({
            columnId: movedcolumnId!,
            kanbanId: kanban?._id!,
            userId: user._id,
            oldOrder: index,
            newOrder: targetIndex,
          }),
        ).unwrap();

        // This path always sends oldOrder/newOrder, so the server returns the
        // whole rebuilt board rather than just the renamed column.
        if (!("parentId" in res)) {
          setColumns(res.columns!);
        }

        AtiraToast.success(t("deals.column.move.success"));
      }
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    }
  };

  const subHeaderActions: MenuProps["items"] = useMemo(() => {
    const onCreateKanban = () => {
      if (!canCreateKanban) {
        setSubToUnlock({
          title: t("subToUnlock.specific.kanbansCount", {
            limit: thresholds?.total?.KANBANS,
          }),
          visible: true,
        });
        return;
      }

      setKanbanCreateVisible(true);
    };

    return [
      {
        key: "kanban.create",
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faPlus} color="main" />
            <Text color="main">{t("kanban.subheader.action")}</Text>
          </Flex>
        ),
        onClick: onCreateKanban,
        hide: userPermissions.KANBAN.CREATE !== true,
        "data-testid":
          TestIDs.kanbans.subHeader.menu.dropdown.option("kanban.create"),
      },
      {
        key: "kanban.settings",
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faGear} color="main" />
            <Text color="main">{t("common.settings")}</Text>
          </Flex>
        ),
        onClick: () => setKanbanSettingsVisible(true),
        hide: !hasMultipleKanbans && isMember === true,
        "data-testid":
          TestIDs.kanbans.subHeader.menu.dropdown.option("kanban.settings"),
      },
      {
        key: "kanban.automation",
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faBolt} color="main" />
            <Text color="main">{t("kanban.automation.menu.item")}</Text>
          </Flex>
        ),
        onClick: () => setKanbanAutomationVisible(true),
        hide: userPermissions.KANBAN.UPDATE !== true,
      },
      {
        key: "kanban.activity",
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faTimeline} color="main" />
            <Text color="main">{t("common.activities")}</Text>
          </Flex>
        ),
        onClick: () => setKanbanActivityVisible(true),
      },
      {
        key: "kanban.delete",
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faTrash} color="main" />
            <Text color="main">{t("common.delete")}</Text>
          </Flex>
        ),
        onClick: () => setKanbanDeleteVisible(true),
        hide: !hasMultipleKanbans,
        "data-testid":
          TestIDs.kanbans.subHeader.menu.dropdown.option("kanban.delete"),
      },
      {
        key: "export_import",
        onClick: () => setExportModalVisible(true),
        hide: isMember === true,
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faFile} color="main" />
            <Text color="main">{t("common.export")}</Text>
          </Flex>
        ),
      },
    ].filter((action) => !action.hide);
  }, [
    canCreateKanban,
    hasMultipleKanbans,
    isMember,
    setKanbanCreateVisible,
    setKanbanSettingsVisible,
    setKanbanAutomationVisible,
    setKanbanActivityVisible,
    setKanbanDeleteVisible,
    setSubToUnlock,
    t,
    thresholds?.total?.KANBANS,
    userPermissions.KANBAN.CREATE,
    userPermissions.KANBAN.UPDATE,
  ]);

  useEffect(() => {
    if (i18n.language === AppLangs.AR) {
      if (kanbanWrapperRef.current) {
        kanbanWrapperRef.current.scroll({
          left: kanbanWrapperRef.current.scrollWidth,
          behavior: "smooth",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kanbanWrapperRef.current]);

  useEffect(() => {
    const useFilter = (
      location.state as {
        useFilter?: GetUserKanbanDto & { stage?: KanbanColumnStageType };
      }
    )?.useFilter;

    if (!isEmpty(useFilter)) {
      const { dateRange, scope, dateField, stage } = useFilter!;

      formHeaderMethods.setValue("dateRange", dateRange);
      formHeaderMethods.setValue("scope", scope);
      formHeaderMethods.setValue("dateField", dateField);
      formHeaderMethods.setValue("kanbanId", undefined);
      pendingScrollStage.current = stage;

      window.history.replaceState(
        { ...location.state, useFilter: undefined },
        "",
      );
    }

    dispatch(
      kanbanActions.getUserKanban({
        userId: user._id,
        kanbanId: useFilter ? undefined : kanban?._id,
        ...omitBy(omit(useFilter, ["stage", "kanbanId"]), isNil),
      }),
    );
    dispatch(permissionActions.getMembers({ userId: user._id }));
    dispatch(kanbanActions.getUserKanbansMeta({ userId: user._id }));
    dispatch(thresholdActions.getThresholds({ userId: user._id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (kanban) {
      setColumns(kanban?.columns || []);
    }
  }, [kanban, kanban?.columns]);

  // Scrolls to the column a report tile counted, once the board has loaded
  // far enough to have it.
  useEffect(() => {
    if (!pendingScrollStage.current || !columns.length) {
      return;
    }

    const target = columns.find(
      (c) => c.stageType === pendingScrollStage.current,
    );
    const el = target && columnRefs.current.get(target._id);

    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
      pendingScrollStage.current = undefined;
    }
  }, [columns]);

  if (!columns.length && kanbanLoading) {
    return <SpinnerFullScreen />;
  }

  return (
    <FormProvider {...formHeaderMethods}>
      <Flex flexDirection="column" flex={1} minHeight={"40rem"}>
        <SubHeader
          extra={
            subHeaderActions.length ? (
              <AtiraDropdown
                items={subHeaderActions}
                placement="bottomRight"
                trigger={["click"]}
                destroyOnHidden
                dataTestId={TestIDs.kanbans.subHeader.menu.dropdown.open}
              >
                <Button
                  icon={faBars}
                  color="black"
                  padding="s"
                  backgroundColor="white"
                  hoverBackgroundColor="lightMain"
                  hover
                >
                  {t("common.options")}
                </Button>
              </AtiraDropdown>
            ) : null
          }
          title={t("common.deals") + " / " + kanban?.name}
          icon={EntityIcons.Deals}
        />

        <KanbanHeader />

        <DNDContext
          onDragStart={onDragStart}
          onDragEnd={
            draggedItemType === DraggedItemTypes.CARD
              ? onDragEndCard
              : onDragEndColumn
          }
        >
          <ColumnsWrapper ref={kanbanWrapperRef}>
            {userPermissions.KANBAN.UPDATE && i18n.language === AppLangs.AR ? (
              <KanbanColumnAdd canAddColumn={canAddColumn} />
            ) : null}

            {(i18n.language === AppLangs.AR
              ? [...columns].reverse()
              : columns
            ).map((col, i) => (
              <DNDSortableDroppableContext
                id={col._id}
                key={col._id}
                items={col.cards || []}
              >
                <Flex
                  ref={(el: HTMLDivElement | null) => {
                    if (el) {
                      columnRefs.current.set(col._id, el);
                    } else {
                      columnRefs.current.delete(col._id);
                    }
                  }}
                  opacity={activeItemId === col._id ? "0.5" : "1"}
                  height={"98%"}
                >
                  <KanbanColumn
                    isLast={col.order === columns.length - 1}
                    onMove={(dir) =>
                      moveColumn(
                        i18n.language === AppLangs.AR
                          ? // reverse the index when in Arabic to reflect the reversed order of columns
                            columns.length - 1 - i
                          : i,
                        dir,
                      )
                    }
                    column={col}
                  />
                </Flex>
              </DNDSortableDroppableContext>
            ))}

            {userPermissions.KANBAN.UPDATE && i18n.language !== AppLangs.AR ? (
              <KanbanColumnAdd canAddColumn={canAddColumn} />
            ) : null}
          </ColumnsWrapper>

          <DragOverlay
            dropAnimation={{
              duration: 500,
              easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
            }}
          >
            {renderDragOverlay()}
          </DragOverlay>
        </DNDContext>

        <KanbanDeleteModal
          open={kanbanDeleteVisible}
          onClose={() => setKanbanDeleteVisible(false)}
        />
      </Flex>
    </FormProvider>
  );
};

export default KanbanRoute;
